import React, { useEffect, useRef, useState } from 'react';

const FORMATS = ['qr_code', 'code_128', 'code_39', 'ean_13', 'data_matrix'];
const hasDetector = () => typeof window !== 'undefined' && 'BarcodeDetector' in window;

/**
 * Reads a batch label: a handheld scanner types the code and presses Enter into
 * the box, and a phone can use its camera where the browser can decode codes
 * (Chrome on Android does). `onCode` returns a promise of { ok, message }.
 */
export default function ScanBar({ onCode, disabled = false }) {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [camera, setCamera] = useState(false);
  const input = useRef(null);

  const submit = async (text) => {
    const value = String(text || '').trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      const r = await onCode(value);
      setResult(r);
      if (r.ok && navigator.vibrate) navigator.vibrate(60);
      if (!r.ok && navigator.vibrate) navigator.vibrate([80, 60, 80]);
    } finally {
      setBusy(false);
      setCode('');
      if (input.current) input.current.focus();
    }
  };

  return (
    <div className="scan-bar">
      <div className="inline" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input ref={input} className="scan-input" value={code} autoFocus disabled={disabled}
          placeholder="Scan a batch label, or type the batch" aria-label="Scan a batch label"
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(code); } }} />
        <button type="button" className="btn secondary" disabled={disabled || busy || !code.trim()} onClick={() => submit(code)}>Check</button>
        {hasDetector() && (
          <button type="button" className="btn secondary" disabled={disabled} onClick={() => setCamera(true)}>
            <span aria-hidden="true">📷</span> Camera
          </button>
        )}
      </div>
      {result && (
        <div className={`scan-result ${result.ok ? 'ok' : 'bad'}`} role="status" aria-live="polite">
          {result.ok ? '✓ ' : '✕ '}{result.message}
        </div>
      )}
      {camera && <CameraScanner onClose={() => setCamera(false)} onCode={(c) => { setCamera(false); submit(c); }} />}
    </div>
  );
}

function CameraScanner({ onCode, onClose }) {
  const video = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let stream = null;
    let timer = null;
    let live = true;
    (async () => {
      try {
        const supported = window.BarcodeDetector.getSupportedFormats
          ? await window.BarcodeDetector.getSupportedFormats() : FORMATS;
        const detector = new window.BarcodeDetector({ formats: FORMATS.filter((f) => supported.includes(f)) });
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        if (!live) return;
        video.current.srcObject = stream;
        await video.current.play();
        const tick = async () => {
          if (!live) return;
          try {
            const found = await detector.detect(video.current);
            if (found.length && found[0].rawValue) { onCode(found[0].rawValue); return; }
          } catch { /* a frame that could not be read; try the next */ }
          timer = setTimeout(tick, 250);
        };
        tick();
      } catch (e) {
        setError(e && e.name === 'NotAllowedError'
          ? 'The camera was not allowed. Allow it in the browser, or use a scanner or the box.'
          : 'The camera could not be started on this device.');
      }
    })();
    return () => {
      live = false;
      clearTimeout(timer);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="scrim" onClick={onClose}>
      <div className="camera-box" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Scan with the camera">
        {error ? <div className="scan-result bad">{error}</div> : <video ref={video} playsInline muted />}
        <div className="camera-frame" aria-hidden="true" />
        <button className="btn secondary" style={{ marginTop: 10, width: '100%' }} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}
